import { type RefObject, useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { isCrawlerUserAgent } from "@/lib/crawler-user-agent";
import { type Product, getProductAnyId } from "@/lib/product-utils";
import {
  addToSimilarProductsCache,
  fetchSimilarProducts,
  getFromSimilarProductsCache,
} from "@/lib/similar-products";

interface UseSimilarProductsResult {
  relatedProducts: Product[];
  loadingRelated: boolean;
}

// Lead margin so the rail is loading slightly before it enters view.
const RAIL_VISIBILITY_ROOT_MARGIN = "200px 0px";

// Any of these marks a real visitor. A renderer that only loads the page
// (headless browsers, prerenderers) produces none of them, whatever its
// viewport size, so intersection alone is never enough to issue the search.
const INTERACTION_EVENTS = [
  "scroll",
  "wheel",
  "touchstart",
  "pointerdown",
  "pointermove",
  "keydown",
];

export interface UseSimilarProductsOptions {
  /**
   * Element that marks where the related-products rail renders. The search is
   * issued only once this element is in (or near) the viewport AND the visitor
   * has interacted with the page (scroll, pointer, touch or key). Headless
   * renderers that load the page but never interact therefore never trigger
   * the search; a shopper who scrolls to the rail sees it load as before.
   * When no element is provided, or IntersectionObserver is unavailable, the
   * search runs on mount as it always did.
   */
  sentinelRef?: RefObject<Element | null>;
}

function useUserInteracted(enabled: boolean): boolean {
  const [interacted, setInteracted] = useState(!enabled);

  useEffect(() => {
    if (!enabled || interacted) return;
    const markInteracted = () => setInteracted(true);
    const options: AddEventListenerOptions = { passive: true, once: true };
    for (const eventName of INTERACTION_EVENTS) {
      window.addEventListener(eventName, markInteracted, options);
    }
    return () => {
      for (const eventName of INTERACTION_EVENTS) {
        window.removeEventListener(eventName, markInteracted);
      }
    };
  }, [enabled, interacted]);

  return interacted;
}

function useSentinelVisible(
  sentinelRef: RefObject<Element | null> | undefined,
  // The PDP renders the sentinel only once the product is available, so the
  // observer must be (re)attached whenever the product changes, not just on mount.
  product: Product | null
): boolean {
  const deferred =
    sentinelRef !== undefined &&
    typeof window !== "undefined" &&
    typeof window.IntersectionObserver === "function";
  const [intersecting, setIntersecting] = useState(!deferred);
  const interacted = useUserInteracted(deferred);

  useEffect(() => {
    if (!deferred || intersecting) return;
    const element = sentinelRef?.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIntersecting(true);
          observer.disconnect();
        }
      },
      { rootMargin: RAIL_VISIBILITY_ROOT_MARGIN, threshold: 0 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [deferred, intersecting, sentinelRef, product]);

  return intersecting && interacted;
}

export function useSimilarProducts(
  product: Product | null,
  options: UseSimilarProductsOptions = {}
): UseSimilarProductsResult {
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const { loading: authLoading } = useAuth();
  const railVisible = useSentinelVisible(options.sentinelRef, product);

  useEffect(() => {
    if (!product || authLoading || !railVisible || isCrawlerUserAgent(navigator.userAgent)) {
      return;
    }

    const cacheKey = getProductAnyId(product);
    const canUseCache = cacheKey !== "unknown";

    if (canUseCache) {
      const cached = getFromSimilarProductsCache(cacheKey);
      if (cached) {
        setRelatedProducts(cached);
        setLoadingRelated(false);
        return;
      }
    }

    setRelatedProducts([]);
    let stale = false;

    const loadSimilarProducts = async () => {
      setLoadingRelated(true);
      try {
        const similar = await fetchSimilarProducts(product);
        if (stale) return;
        if (canUseCache) {
          addToSimilarProductsCache(cacheKey, similar);
        }
        setRelatedProducts(similar);
      } catch (error) {
        console.error("Failed to load similar products:", error);
      } finally {
        if (!stale) {
          setLoadingRelated(false);
        }
      }
    };
    loadSimilarProducts();
    return () => {
      stale = true;
    };
  }, [product, authLoading, railVisible]);

  return { relatedProducts, loadingRelated };
}
